import React from 'react';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { alpha } from '@mui/material/styles';
import clsx from 'clsx';
import {
  FormControl,
  Grid,
  FormHelperText,
  FormLabel,
  RadioGroup,
  FormGroup,
  Checkbox,
  FormControlLabel,
  Radio,
  Typography,
  Button,
  IconButton,
  Divider,
  Link,
  TextField,
  Tooltip,
  Box,
  Drawer,
} from '@mui/material';
import Alert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import AlertDialog from './AlertDialog';
import {CutCopyPasteTextField} from '../CommonComponents/CustomInputs';
import {mdiInformationOutline} from '@mdi/js';
import Autocomplete from '@mui/material/Autocomplete';
import Icon from '@mdi/react';
import {DialogOutputMethodHelp} from '../CommonComponents/DialogBox';
import Snackbar from '../CommonComponents/Snackbar';
import _ from "lodash"
import { useTranslation } from "react-i18next";
import {OUTPUT_FILE_DEFAULTS} from '../../StateObjects/requestFormDefaults';
import {Auth} from '../../Api/auth';
import AddSupportingFiles from "./AddSupportingFiles";

import { useMsal } from "@azure/msal-react";

const StyledDrawer = styled(Drawer)(({theme}) => ({
  '& .MuiDrawer-paper.MuiDrawer-paper': {
    width: '100%',
    maxWidth: theme.spacing(75),
    boxSizing: 'border-box',
  },
}));

function ModifyFileForm(props) {

  const {t} = useTranslation();

  const {instance}=useMsal();

  const index = props.index !== null ? props.index : props.formData.outputFiles.length;
  const [state, setState] = React.useState(props.index !== null ? props.formData.outputFiles[index] : _.cloneDeep(OUTPUT_FILE_DEFAULTS));
  const [usedOutputFiles, setUsedOutputFiles] = React.useState([]);
  const [dialogAlert, setDialogAlert] = React.useState({
    open: false,
    event: null
  });
  const [dialogOutputMethodHelp, setDialogOutputMethodHelp] = React.useState(false);
  const [snackbarState, setSnackbarState] = React.useState({
    open: false,
    severity: "success",
    message: ""
  });
  
  const fileFlag = state.outputFile==="";

  const yesAnswer = t("requestForm.yes");
  const noAnswer = t("requestForm.no");

  const handleStateValueChange = (e) => {
    setState({
      ...state,
      [e.target.name]: e.target.value,
    });

    if(Boolean(props.errors.filesList?.[`outputFiles.${index}.${e.target.name}`]) && e.target.value!==""){
      let errs = _.cloneDeep(props.errors);
      delete errs.filesList[`outputFiles.${index}.${e.target.name}`];
      props.setErrors(errs);
    }
  };

  const getConditionalNewValues=(e)=>{
    switch(e.target.name){
      case "includeWeightVariable":
        if(e.target.value!== "Yes"){
          return {
            includeWeightVariable: e.target.value,
            weightVariableName: "",
            weightVariableType: ""
          };
        }else{
          return {
            includeWeightVariable: e.target.value
          };
        }
      case "linkedData":
        if(e.target.value!== "Yes"){
          return {
            linkedData: e.target.value,
            linkedDataDescription: ""
          };
        }else{
          return {
            linkedData: e.target.value
          };
        }
      case "descriptiveStats":
        if(e.target.value!== "Yes"){
          return {
            descriptiveStats: e.target.value,
            outputLabelled: "",
            minCellSize: ""
          };
        }else{
          return {
            descriptiveStats: e.target.value
          };
        }
      case "includeMatrix":
        if(e.target.value!== "Yes"){
          return {
            includeMatrix: e.target.value,
            continuousVariables: "",
            dichotomousVariables: "",
            correlatedVariables: ""
          };
        }else{
          return {
            includeMatrix: e.target.value
          };
        }
      case "roundingOutput":
        if(e.target.value!== "Yes"){
          return {
            roundingOutput: e.target.value,
            roundingOutputDescription: ""
          };
        }else{
          return {
            roundingOutput: e.target.value
          };
        }
      case "outputContinuousVariables":
        if(e.target.value!== "Yes"){
          return {
            outputContinuousVariables: e.target.value,
            continuousVariablesSensitivity: ""
          }
        }
        else{
          return{
            outputContinuousVariables:e.target.value
          }
        }
      
      case "economicDataOutput":
        if(e.target.value!== "Yes"){
          return {
            economicDataOutput: e.target.value,
            uniqueDataUnits: "",
            outputContinuousVariables: "",
            continuousVariablesSensitivity: ""
          };
        }else{
          return {
            economicDataOutput: e.target.value
          };
        }
      default:
        return {
          [e.target.name]: e.target.value
        }
    }
  };

  const handleConditionalValueChange=(e)=>{
    let newValues = getConditionalNewValues(e);
    setState({
      ...state,
      ...newValues
    });

    let errs=_.cloneDeep(props.errors);
    let newErrs=false;
    Object.keys(newValues).forEach(k=>{
      if(props.errors.filesList?.[`outputFiles.${index}.${k}`]){
        delete errs.filesList[`outputFiles.${index}.${k}`];
        newErrs=true;
      }
    });
    if(newErrs){
      props.setErrors(errs);
    }
  };
  
  const handleOutputChange = (e)=> {
    setDialogAlert({
      open: false,
      event: null
    });
    setState({
      ...OUTPUT_FILE_DEFAULTS,
      outputFile: e.target.value
    })

    if("filesList" in props.errors){
      let errs = _.cloneDeep(props.errors);
      Object.keys(props.errors.filesList).forEach(k=>{
        if(k.includes(`outputFiles.${index}.`)){
          delete errs.filesList[k];
        }
      });
      props.setErrors(errs);
    }
  };

  const handleAlertOpen = (e)=> {
    let val = {...state};
    let defaults = {...OUTPUT_FILE_DEFAULTS};
    defaults.outputFile = val.outputFile;
    
    if(val.outputFile === "" || _.isEqual({...defaults, ...val}, defaults)){
      handleStateValueChange(e);
    }else{
      setDialogAlert({
        open: true,
        event: {target:{name:e.target.name, value:e.target.value}}
      });
    }
  };

  const handleMandatoryChange = (e, target, files)=> {
    let newSupportingFiles = {...state.supportingFiles};
    if(target === null || e.target.value !== target){
      for(let file of files){
        newSupportingFiles[file]=[];
      }
    }
    let newValues = getConditionalNewValues(e);

    let errs=_.cloneDeep(props.errors);
    Object.keys(newValues).forEach(k=>{
      if(props.errors.filesList?.[`outputFiles.${index}.${k}`]){
        delete errs.filesList[`outputFiles.${index}.${k}`];
      }
    });

    if(props.errors.filesList){
      files.forEach(f=>{
        Object.keys(props.errors.filesList).forEach(k=>{
          if(k.startsWith(`outputFiles.${index}.supportingFiles.${f}.`)){
            delete errs.filesList[k];
          }
        });
      });
    }

    setState({
      ...state,
      ...newValues,
      supportingFiles: newSupportingFiles
    });
    props.setErrors(errs);
  };

  const handleOutputMethodChange=(e)=>{
    let newValues = {};
    let errs=_.cloneDeep(props.errors);
    
    if(e.target.name==="otherMethod" && !e.target.checked){
      newValues = {
        outputMethodDescription: ""
      };
      if(props.errors.filesList?.[`outputFiles.${index}.outputMethodDescription`]){
        delete errs.filesList[`outputFiles.${index}.outputMethodDescription`];
      }
    }

    if(props.errors.filesList?.[`outputFiles.${index}.outputMethods`]){
      delete errs.filesList[`outputFiles.${index}.outputMethods`];
    }

    setState({
      ...state,
      outputMethods: {...state.outputMethods, [e.target.name]: e.target.checked},
      ...newValues
    });
    props.setErrors(errs);
  }

  const validateOutput=(fn)=>{
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({formData:state})
    };
    Auth.authorizedFetch("/api/validateOutput", requestOptions,instance)
    .then(res=>res.json())
    .then(data=>{
      //clear errors
      let errs=_.cloneDeep(props.errors);
      
      if(props.errors.filesList){
        Object.keys(props.errors.filesList).forEach(k=>{
          const match = k.match(/\d+/);
          if(match && k.startsWith("outputFiles.")){
            const j = Number(match[0]);
            if(j===index){
             
              delete errs.filesList[k];
            }
          }
        });
      }

     

      if(data.statusCode===400 && Object.prototype.hasOwnProperty.call(data.data, "formErrors")){
        let newErrs={};
        Object.keys(data.data.formErrors).forEach(k=>{
          newErrs[`outputFiles.${index}.${k}`]=data.data.formErrors[k];
        });

        if(props.errors.filesList?.outputFiles){
          delete errs.filesList["outputFiles"]
        }

        props.setErrors({
          ...errs,
          filesList: {
            ...errs.filesList,
            ...newErrs
          }
        });

        fn();
      }else if(data.statusCode===200){

        if(props.errors.filesList?.outputFiles){
          delete errs.filesList["outputFiles"];
          
        }
        props.setErrors(errs);

        fn();
      }else{
        setSnackbarState({open: true, severity: "error", message: t("applicationErrorMessage.generic.dataError")});
      }
    })
    .catch(()=>{
      setSnackbarState({open: true, severity: "error", message: t("applicationErrorMessage.generic.dataError")});
    });
  }

  React.useEffect(()=>{
    //removed already selected files from the list
    let usedOutputs=[];
    for(let i=0; i<props.formData.outputFiles.length; i++){
      if(i !== index){
        const file = props.formData.outputFiles[i];
        if(file.outputFile !== ""){
          usedOutputs.push(file.outputFile);
        }
      }
    }
    setUsedOutputFiles(usedOutputs);
  }, [props.formData.outputFiles]);

  let errorCount = 0;
  if(props.errors.filesList && index!==null){
    Object.keys(props.errors.filesList).forEach(k=>{
      const match = k.match(/\d+/);
        if(match && Number(match)===index && k.startsWith("outputFiles.")){
          errorCount++;
        }
    });
  }

  const supportingFilesSection=(fileType, addBtnClass, tooltip,mandatorySection=true,extraParagraph=false,extraNote)=>{
    let fileTypeDisplay=null;
    if(fileType.includes("other")){
      fileTypeDisplay="additionalDetails";
    }

    //this is to handle addition of new file type as old record might not have proper filetype in their save data
    if (state.supportingFiles[fileType]==undefined){
      let newSupportingFiles = _.cloneDeep(state.supportingFiles);
      newSupportingFiles[fileType]=[]
      setState({...state,supportingFiles:newSupportingFiles})
    }

    return <AddSupportingFiles 
      step="filesList"
      outputIndex={index}
      filesList={props.filesList}
      supportingFiles={state.supportingFiles}
      fileType={fileType}
      fileTypeDisplay={fileTypeDisplay}
      readOnly={props.readOnly || props.analystEditFlag || fileFlag}
      errors={props.errors}
      setErrors={props.setErrors}
      handleUpdateSupportingFiles={(newVal)=>{
        setState({...state, supportingFiles: newVal});
      }}
      addBtnClass={addBtnClass}
      tooltip={tooltip}
      mandatorySection={mandatorySection}
      extraParagraph={extraParagraph}
      extraNote={extraNote}
    />
  }

  return (
    <Root>
      <div className={classes.drawerHeader}>
        <Typography variant="h6" component="h2" className={classes.title}>
          {props.isAnalyst ?
            t("requestForm.modifyFile.reviewOutputFile") :
            props.readOnly ? 
              t("requestForm.modifyFile.viewOutputFile") :
              (props.index !== null ?
                t("requestForm.modifyFile.editOutputFile") : 
                t("requestForm.modifyFile.addOutputFile"))
          }
        </Typography>
        <IconButton
          id="close-output-btn"
          aria-label={t('dialog.close')}
          className={classes.margin}
          edge="end"
          onClick={()=>props.toggleDrawer(false)}
          size="large">
          <CloseIcon />
        </IconButton>
      </div>
      <div className={classes.drawerContent}>
        {Boolean(errorCount) &&
          <Alert sx={{mb: 2}} severity="error">
            {t("requestForm.filesList.outputError", {count:errorCount})}
          </Alert>
        }
        <Typography variant="subtitle2" component="h3" sx={{mb: 3}}>
          {t('requestForm.modifyFile.selectOutput')}
        </Typography>
        <Autocomplete
          sx={{mb: 3}}
          componentsProps={{
            clearIndicator: {tabIndex: 0},
          }}
          classes={{
            option: classes.autocompleteOption,
          }}
          id="outputFile"
          options={props.filesList.filter(i=>!usedOutputFiles.includes(i))}
          renderInput={(params) => {
            let readOnlyProps = props.readOnly || props.analystEditFlag ?
              {InputProps: {readOnly: true}} : {};
            return (
              <TextField
                {...params}
                multiline
                minRows="1"
                label={t("requestForm.modifyFile.outputFile")}
                required={true}
                variant="outlined"
                error={Boolean(t(props.errors.filesList?.[`outputFiles.${index}.outputFile`]))}
                helperText={t(props.errors.filesList?.[`outputFiles.${index}.outputFile`])}
                {...readOnlyProps}
              />
          );}}
          value={state.outputFile==="" ? null : state.outputFile}
          onChange={(event, newValue) => {
            if(newValue===null){
              newValue="";
            }
            handleAlertOpen({target: {name: "outputFile", value: newValue}});
          }}
          disableClearable={true}
        />
        {state.outputFile !== "" &&
          <Box className={classes.indentedSection} sx={{mb: 3}}>
            <Box sx={{mb: 3}}>
              <CutCopyPasteTextField              
                margin="dense"
                id="sheetName"
                name="sheetName"
                label={t("requestForm.modifyFile.sheetName")}
                variant="outlined"
                helperText={t("requestForm.modifyFile.sheetNameHelper")}
                fullWidth
                disabled={fileFlag}
                value={state.sheetName}
                onChange={handleStateValueChange}
                inputProps={{readOnly: props.readOnly}}
              />
            </Box>
          </Box>
        }
        <Divider sx={{mb: 3}} />
        <Typography variant="subtitle2" component="h3" sx={{mb: 3}}>
          {t('requestForm.modifyFile.outputDetails')}
        </Typography>
        <FormControl 
          component="fieldset" 
          required 
          sx={{mb: 2}}
          disabled={fileFlag}
          error={Boolean(props.errors.filesList?.[`outputFiles.${index}.outputMethods`])}
          aria-describedby="output-meth-helper"
        >
          <FormLabel component="legend" id="output-meth-legend">
            {t("requestForm.modifyFile.outputMethod")}{' '}
            <span className="screen-reader-text">{t("inputs.required")}</span>
          </FormLabel>
          <FormHelperText id="output-meth-helper">
            {props.errors.filesList?.[`outputFiles.${index}.outputMethods`] ? 
              t(props.errors.filesList?.[`outputFiles.${index}.outputMethods`]) :
              <>
                {t("requestForm.modifyFile.needHelp")}&nbsp;
                <Link
                  id="outputMethod-help-link"
                  underline="always"
                  variant="caption"
                  color="inherit"
                  onClick={() => setDialogOutputMethodHelp(true)}
                  component="button"
                  disabled={fileFlag}
                >
                  {t("requestForm.modifyFile.outputMethodLearn")}
                </Link>
              </>
            }
          </FormHelperText>
          <FormGroup role="group" aria-labelledby="output-meth-legend">
            <FormControlLabel
              id="descriptiveMethod-checkbox"
              control={
                <Checkbox
                  color="primary"
                  checked={state.outputMethods.descriptiveMethod}
                  onChange={props.readOnly ? null : handleOutputMethodChange}
                  name="descriptiveMethod"
                />
              }
              label={t("requestForm.modifyFile.methods.descriptiveMethod")}
            />
            <FormControlLabel
              id="scalingMethod-checkbox"
              control={
                <Checkbox
                  color="primary"
                  checked={state.outputMethods.scalingMethod}
                  onChange={props.readOnly ? null : handleOutputMethodChange}
                  name="scalingMethod"
                />
              }
              label={t("requestForm.modifyFile.methods.scalingMethod")}
            />
            <FormControlLabel
              id="graphsMethod-checkbox"
              control={
                <Checkbox
                  color="primary"
                  checked={state.outputMethods.graphsMethod}
                  onChange={props.readOnly ? null : handleOutputMethodChange}
                  name="graphsMethod"
                />
              }
              label={t("requestForm.modifyFile.methods.graphsMethod")}
            />
            <FormControlLabel
              id="regressionMethod-checkbox"
              control={
                <Checkbox
                  color="primary"
                  checked={state.outputMethods.regressionMethod}
                  onChange={props.readOnly ? null : handleOutputMethodChange}
                  name="regressionMethod"
                />
              }
              label={t("requestForm.modifyFile.methods.regressionMethod")}
            />
            <FormControlLabel
              id="modelingMethod-checkbox"
              control={
                <Checkbox
                  color="primary"
                  checked={state.outputMethods.modelingMethod}
                  onChange={props.readOnly ? null : handleOutputMethodChange}
                  name="modelingMethod"
                />
              }
              label={t("requestForm.modifyFile.methods.modelingMethod")}
            />
            <FormControlLabel
              id="otherMethod-checkbox"
              control={
                <Checkbox
                  color="primary"
                  checked={state.outputMethods.otherMethod}
                  onChange={props.readOnly ? null : handleOutputMethodChange}
                  name="otherMethod"
                />
              }
              label={t("requestForm.modifyFile.methods.otherMethod")}
            />
          </FormGroup>
        </FormControl>
        {state.outputMethods.otherMethod &&
          <Box className={classes.indentedSection} sx={{mb:3}}>
            <Box sx={{mb: 3}}>
              <CutCopyPasteTextField
                id="outputMethodDescription"
                name="outputMethodDescription"
                margin="dense"
                label={t("requestForm.modifyFile.outputMethodDescription")}
                variant="outlined"
                fullWidth
                required
                multiline
                minRows="3"
                value={state.outputMethodDescription}
                onChange={handleStateValueChange}
                inputProps={{readOnly: props.readOnly}}
                error={Boolean(props.errors.filesList?.[`outputFiles.${index}.outputMethodDescription`])}
                helperText={t(props.errors.filesList?.[`outputFiles.${index}.outputMethodDescription`])}
              />
            </Box>
          </Box>
        }
        <Box sx={{mb: 3}}>
          <CutCopyPasteTextField
            margin="dense"
            id="datasetName"
            name="datasetName"
            label={t("requestForm.modifyFile.datasetName")}
            variant="outlined"
            fullWidth
            disabled={fileFlag}
            value={state.datasetName}
            onChange={handleStateValueChange}
            inputProps={{readOnly: props.readOnly}}
            required
            error={Boolean(props.errors.filesList?.[`outputFiles.${index}.datasetName`])}
            helperText={props.errors.filesList?.[`outputFiles.${index}.datasetName`] ? 
              t(props.errors.filesList?.[`outputFiles.${index}.datasetName`]) :
              t("requestForm.modifyFile.datasetNameHelper")
            }
          />
        </Box>
        <Grid component="span" sx={{mb: 3}} container>
          <Grid component="span" xs item>
            <CutCopyPasteTextField
              id="sampleUsed"
              name="sampleUsed"
              margin="dense"
              label={t("requestForm.modifyFile.sampleUsed")}
              variant="outlined"
              fullWidth
              value={state.sampleUsed}
              onChange={handleStateValueChange}
              disabled={fileFlag}
              inputProps={{readOnly: props.readOnly}}
              required
              error={Boolean(props.errors.filesList?.[`outputFiles.${index}.sampleUsed`])}
              helperText={props.errors.filesList?.[`outputFiles.${index}.sampleUsed`] ?
                t(props.errors.filesList[`outputFiles.${index}.sampleUsed`]) :
                t("requestForm.modifyFile.sampleUsedHelper")
              }
            />
          </Grid>
          <Grid component="span" className={classes.textFieldInfoIcon} item>
            <Tooltip title={t("requestForm.modifyFile.sampleUsedTooltip")} arrow describeChild>
              <IconButton
                aria-label={t('requestForm.toolTipMoreInformation')}
                edge="end"
                size="large">
                <Icon path={mdiInformationOutline} size={1} />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
        <Box sx={{mb: 3}}>
          <CutCopyPasteTextField
            id="geographyLevel"
            name="geographyLevel"
            margin="dense"
            label={t("requestForm.modifyFile.geographyLevel")}
            variant="outlined"
            fullWidth
            value={state.geographyLevel}
            onChange={handleStateValueChange}
            disabled={fileFlag}
            inputProps={{readOnly: props.readOnly}}
            required
            error={Boolean(props.errors.filesList?.[`outputFiles.${index}.geographyLevel`])}
            helperText={props.errors.filesList?.[`outputFiles.${index}.geographyLevel`] ?
              t(props.errors.filesList[`outputFiles.${index}.geographyLevel`]) :
              t("requestForm.modifyFile.geographyLevelHelper")
            }
          />
        </Box>
        <Divider sx={{mb: 3}} />
        <Typography variant="subtitle2" component="h3" sx={{mb: 3}}>
          {t('requestForm.modifyFile.weightedVariable')}
        </Typography>
        <FormControl
          sx={{mb: 2}}
          component="fieldset"
          required
          disabled={fileFlag} 
          error={Boolean(props.errors.filesList?.[`outputFiles.${index}.includeWeightVariable`])}
          aria-describedby="includeWeightVariable-helperText"
        >
          <FormLabel component="legend">
            {t("requestForm.modifyFile.includeWeightVariable")}
            <span className="screen-reader-text">{t("inputs.required")}</span>
          </FormLabel>
          <FormHelperText id="includeWeightVariable-helperText">
            {t(props.errors.filesList?.[`outputFiles.${index}.includeWeightVariable`])}
          </FormHelperText>
          <RadioGroup
            id="includeWeightVariable"
            name="includeWeightVariable"
            value={state.includeWeightVariable}
            onChange={props.readOnly ? null : handleConditionalValueChange}
          >
            <FormControlLabel
              value="Yes"
              control={<Radio color="primary" />}
              label={yesAnswer}
            />
            <FormControlLabel
              value="No"
              control={<Radio color="primary" />}
              label={noAnswer}
            />
          </RadioGroup>
        </FormControl>
        {state.includeWeightVariable === 'Yes' && (
          <Box className={classes.indentedSection} sx={{mb:3}}>
            <Box sx={{mb: 3}}>
                <CutCopyPasteTextField
                  id="weightVariableName"
                  name="weightVariableName"
                  margin="dense"
                  label={t("requestForm.modifyFile.weightVariableName")}
                  variant="outlined"
                  required
                  fullWidth
                  value={state.weightVariableName}
                  onChange={handleStateValueChange}
                  error={Boolean(props.errors.filesList?.[`outputFiles.${index}.weightVariableName`])}
                  helperText={t(props.errors.filesList?.[`outputFiles.${index}.weightVariableName`])}
                  inputProps={{readOnly: props.readOnly}}
                />
              </Box>
            <FormControl
              sx={{mb: 2}}
              component="fieldset"
              required
              error={Boolean(props.errors.filesList?.[`outputFiles.${index}.weightVariableType`])}
              aria-describedby="weightVariableType-helperText"
            >
              <FormLabel component="legend">
                {t("requestForm.modifyFile.weightVariableType")}
                <span className="screen-reader-text">{t("inputs.required")}</span>
              </FormLabel>
              <FormHelperText id="weightVariableType-helperText">
                {t(props.errors.filesList?.[`outputFiles.${index}.weightVariableType`])}
              </FormHelperText>
              <RadioGroup
                id="weightVariableType"
                name="weightVariableType"
                onChange={props.readOnly ? null : handleStateValueChange}
                value={state.weightVariableType}
              >
                <FormControlLabel
                  value="Yes"
                  control={<Radio color="primary" />}
                  label={yesAnswer}
                />
                <FormControlLabel
                  value="No"
                  control={<Radio color="primary" />}
                  label={noAnswer}
                />
              </RadioGroup>
            </FormControl>
          </Box>
        )}
        <Divider sx={{mb: 3}} />
        <Typography variant="subtitle2" component="h3" sx={{mb: 3}}>
          {t('requestForm.modifyFile.linkedDataTitle')}
        </Typography>
        <FormControl
          component="fieldset"
          sx={{mb: 2}}
          required
          disabled={fileFlag} 
          error={Boolean(props.errors.filesList?.[`outputFiles.${index}.linkedData`])}
          aria-describedby="linkedData-helperText"
        >
          <FormLabel component="legend">
            {t("requestForm.modifyFile.linkedData")}
            <span className="screen-reader-text">{t("inputs.required")}</span>
          </FormLabel>
          <FormHelperText id="linkedData-helperText">
            {t(props.errors.filesList?.[`outputFiles.${index}.linkedData`])}
          </FormHelperText>
          <RadioGroup
            id="linkedData"
            name="linkedData"
            value={state.linkedData}
            onChange={props.readOnly ? null : handleConditionalValueChange}
          >
            <FormControlLabel
              value="Yes"
              control={<Radio color="primary" />}
              label={yesAnswer}
            />
            <FormControlLabel
              value="No"
              control={<Radio color="primary" />}
              label={noAnswer}
            />
          </RadioGroup>
        </FormControl>
        {state.linkedData === 'Yes' && (
          <Box className={classes.indentedSection} sx={{mb:3}}>
            <Box sx={{mb: 3}}>
              <CutCopyPasteTextField
                id="linkedDataDescription"
                name="linkedDataDescription"
                margin="dense"
                label={t("requestForm.modifyFile.linkedDataDescription")}
                variant="outlined"
                fullWidth
                required
                inputProps={{readOnly: props.readOnly}}
                value={state.linkedDataDescription}
                onChange={handleStateValueChange}
                error={Boolean(props.errors.filesList?.[`outputFiles.${index}.linkedDataDescription`])}
                helperText={props.errors.filesList?.[`outputFiles.${index}.linkedDataDescription`] ?
                  t(props.errors.filesList?.[`outputFiles.${index}.linkedDataDescription`]) : 
                  t("requestForm.modifyFile.linkedDataDescriptionHelper")
                }
              />
            </Box>
          </Box>
        )}

        <Divider sx={{mb: 3}} />
        <Typography variant="subtitle2" component="h3" sx={{mb: 3}}>
          {t('requestForm.modifyFile.descriptiveStatsTitle')}
        </Typography>
        <FormControl
          sx={{mb: 2}}
          component="fieldset"
          required 
          disabled={fileFlag} 
          error={Boolean(props.errors.filesList?.[`outputFiles.${index}.descriptiveStats`])}
          aria-describedby="descriptiveStats-helperText"
        >
          <FormLabel component="legend">
            {t("requestForm.modifyFile.descriptiveStats")}
            <span className="screen-reader-text">{t("inputs.required")}</span>
          </FormLabel>
          <FormHelperText id="descriptiveStats-helperText">
            {t(props.errors.filesList?.[`outputFiles.${index}.descriptiveStats`])}
          </FormHelperText>
          <RadioGroup
            id="descriptiveStats"
            name="descriptiveStats"
            value={state.descriptiveStats}
            onChange={(e)=>props.readOnly ? null : handleMandatoryChange(e, "Yes", ["correctDocFile"])}
          >
            <FormControlLabel
              value="Yes"
              control={<Radio color="primary" />}
              label={yesAnswer}
            />
            <FormControlLabel
              value="No"
              control={<Radio color="primary" />}
              label={noAnswer}
            />
          </RadioGroup>
        </FormControl>
        {state.descriptiveStats === 'Yes' && (
          <Box className={classes.indentedSection} sx={{mb: 3}}>
            <FormControl 
              sx={{mb: 2}} 
              component="fieldset"
              required
              error={Boolean(props.errors.filesList?.[`outputFiles.${index}.outputLabelled`])}
              aria-describedby="outputLabelled-helperText"
            >
              <FormLabel component="legend" className={classes.lineHeight}>
                {t("requestForm.modifyFile.outputLabelled")}
                <span className="screen-reader-text">{t("inputs.required")}</span>
              </FormLabel>
              <FormHelperText id="outputLabelled-helperText">
                {t(props.errors.filesList?.[`outputFiles.${index}.outputLabelled`])}
              </FormHelperText>
              <RadioGroup 
                id="outputLabelled"
                name="outputLabelled"
                onChange={props.readOnly ? null : handleStateValueChange} 
                value={state.outputLabelled}
              >
                <FormControlLabel
                  value="Yes"
                  control={<Radio color="primary" />}
                  label={yesAnswer}
                />
                <FormControlLabel
                  value="No"
                  control={<Radio color="primary" />}
                  label={noAnswer}
                />
              </RadioGroup>
            </FormControl>
            <FormControl 
              sx={{mb: 2}} 
              component="fieldset"
              required
              error={Boolean(props.errors.filesList?.[`outputFiles.${index}.minCellSize`])}
              aria-describedby="minCellSize-helperText"
            >
              <FormLabel component="legend">
                {t("requestForm.modifyFile.minCellSize")}
                <span className="screen-reader-text">{t("inputs.required")}</span>
              </FormLabel>
              <FormHelperText id="minCellSize-helperText">
                {t(props.errors.filesList?.[`outputFiles.${index}.minCellSize`])}
              </FormHelperText>
              <RadioGroup 
                id="minCellSize"
                name="minCellSize"
                onChange={props.readOnly ? null : handleStateValueChange} 
                value={state.minCellSize}
              >
                <FormControlLabel
                  value="Yes"
                  control={<Radio color="primary" />}
                  label={yesAnswer}
                />
                <FormControlLabel
                  value="No"
                  control={<Radio color="primary" />}
                  label={noAnswer}
                />
              </RadioGroup>
            </FormControl>
            {supportingFilesSection("correctDocFile", `${classes.addBtn} ${classes.mb3}`, t("requestForm.modifyFile.correctDocFileTip"))}
          </Box>
        )}
        <Grid component="span" container>
          <Grid component="span" xs item>
            <FormControl
              sx={{mb: 2}}
              component="fieldset"
              required 
              disabled={fileFlag} 
              error={Boolean(props.errors.filesList?.[`outputFiles.${index}.equivalentDescriptiveStats`])}
              aria-describedby="equivalentDescriptiveStats-helperText"
            >
              <FormLabel component="legend" className={classes.tooltipLabel}>
                {t("requestForm.modifyFile.equivalentDescriptiveStats")}
                <span className="screen-reader-text">{t("inputs.required")}</span>
              </FormLabel>
              <FormHelperText id="equivalentDescriptiveStats-helperText">
                {t(props.errors.filesList?.[`outputFiles.${index}.equivalentDescriptiveStats`])}
              </FormHelperText>
              <RadioGroup
                id="equivalentDescriptiveStats"
                name="equivalentDescriptiveStats"
                onChange={(e)=>props.readOnly ? null : handleMandatoryChange(e, "Yes", ["frequencyTablefile"])}
                value={state.equivalentDescriptiveStats}
              >
                <FormControlLabel
                  value="Yes"
                  control={<Radio color="primary" />}
                  label={yesAnswer}
                />
                <FormControlLabel
                  value="No"
                  control={<Radio color="primary" />}
                  label={noAnswer}
                />
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid component="span" className={classes.legendInfoIcon} item>
            <Tooltip title={t("requestForm.modifyFile.equivalentDescriptiveStatsTip")} arrow describeChild>
              <IconButton
                aria-label={t('requestForm.toolTipMoreInformation')}
                edge="end"
                size="large">
                <Icon path={mdiInformationOutline} size={1} />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
        {state.equivalentDescriptiveStats === 'Yes' &&
          <Box className={classes.indentedSection} sx={{mb: 3}}>
            {supportingFilesSection("frequencyTablefile", `${classes.addBtn} ${classes.mb3}`)}
          </Box>
        }
        <Divider sx={{mb: 3}} />
        <Typography component="h3" variant="subtitle2" sx={{mb: 3}}>
          {t("requestForm.modifyFile.dominanceTesting")}
        </Typography>
        <Grid component="span" container>
          <Grid component="span" xs item>
            <FormControl
              sx={{mb: 2}}
              component="fieldset"
              required 
              disabled={fileFlag} 
              error={Boolean(props.errors.filesList?.[`outputFiles.${index}.dominanceTestingOutput`])}
              aria-describedby="dominanceTestingOutput-helperText"
            >
              <FormLabel component="legend" className={classes.tooltipLabel}>
                {t("requestForm.modifyFile.dominanceTestingOutput")}
                <span className="screen-reader-text">{t("inputs.required")}</span>
              </FormLabel>
              <FormHelperText id="dominanceTestingOutput-helperText">
                {t(props.errors.filesList?.[`outputFiles.${index}.dominanceTestingOutput`])}
              </FormHelperText>
              <RadioGroup
                id="dominanceTestingOutput"
                name="dominanceTestingOutput"
                value={state.dominanceTestingOutput}
                onChange={(e)=>props.readOnly ? null : handleMandatoryChange(e, "Yes", ["dominanceTestingFile"])}
              >
                <FormControlLabel
                  value="Yes"
                  control={<Radio color="primary" />}
                  label={yesAnswer}
                />
                <FormControlLabel
                  value="No"
                  control={<Radio color="primary" />}
                  label={noAnswer}
                />
              </RadioGroup>
            </FormControl>
          </Grid>
        </Grid>
        {state.dominanceTestingOutput==="Yes" &&
            <Box className={classes.indentedSection} sx={{mb:3}}>
              {supportingFilesSection("dominanceTestingFile", `${classes.addBtn} ${classes.mb3}`,undefined,true,false,true)}
            </Box>
        }

        <Typography variant="subtitle2" component="h3" sx={{mb: 3}}>
          {t('requestForm.modifyFile.matrixTitle')}
        </Typography>
        <FormControl
          sx={{mb: 2}}
          component="fieldset"
          required 
          disabled={fileFlag} 
          error={Boolean(props.errors.filesList?.[`outputFiles.${index}.includeMatrix`])}
          aria-describedby="includeMatrix-helperText"
        >
          <FormLabel component="legend">
            {t("requestForm.modifyFile.includeMatrix")}
            <span className="screen-reader-text">{t("inputs.required")}</span>
          </FormLabel>
          <FormHelperText id="includeMatrix-helperText">
            {t(props.errors.filesList?.[`outputFiles.${index}.includeMatrix`])}
          </FormHelperText>
          <RadioGroup
            id="includeMatrix"
            name="includeMatrix"
            onChange={(e)=>props.readOnly ? null : handleMandatoryChange(e, null, ["sampleSizeFile", "crossTabFile", "subTotalsFile"])}
            value={state.includeMatrix}
          >
            <FormControlLabel
              value="Yes"
              control={<Radio color="primary" />}
              label={yesAnswer}
            />
            <FormControlLabel
              value="No"
              control={<Radio color="primary" />}
              label={noAnswer}
            />
          </RadioGroup>
        </FormControl>
        {state.includeMatrix === "Yes" &&
          <Box className={classes.indentedSection} sx={{pb: 3, mb: 3}}>
            <FormControl
              sx={{mb: 2}}
              component="fieldset"
              required 
              error={Boolean(props.errors.filesList?.[`outputFiles.${index}.continuousVariables`])}
              aria-describedby="continuousVariables-helperText"
            >
              <FormLabel component="legend">
                {t("requestForm.modifyFile.continuousVariables")}
                <span className="screen-reader-text">{t("inputs.required")}</span>
              </FormLabel>
              <FormHelperText id="continuousVariables-helperText">
                {t(props.errors.filesList?.[`outputFiles.${index}.continuousVariables`])}
              </FormHelperText>
              <RadioGroup
                id="continuousVariables"
                name="continuousVariables"
                onChange={(e)=>props.readOnly ? null : handleMandatoryChange(e, "Yes", ["sampleSizeFile"])}
                value={state.continuousVariables}
              >
                <FormControlLabel
                  value="Yes"
                  control={<Radio color="primary" />}
                  label={yesAnswer}
                />
                <FormControlLabel
                  value="No"
                  control={<Radio color="primary" />}
                  label={noAnswer}
                />
              </RadioGroup>
            </FormControl>
            {state.continuousVariables==="Yes" &&
              <Box className={classes.indentedSection} sx={{mb: 3}}>
                {supportingFilesSection("sampleSizeFile", `${classes.addBtn} ${classes.mb3}`)}
              </Box>
            }
            <FormControl
              sx={{mb: 2}}
              component="fieldset"
              required 
              error={Boolean(props.errors.filesList?.[`outputFiles.${index}.dichotomousVariables`])}
              aria-describedby="dichotomousVariables-helperText"
            >
              <FormLabel component="legend">
                {t("requestForm.modifyFile.dichotomousVariables")}
                <span className="screen-reader-text">{t("inputs.required")}</span>
              </FormLabel>
              <FormHelperText id="dichotomousVariables-helperText">
                {t(props.errors.filesList?.[`outputFiles.${index}.dichotomousVariables`])}
              </FormHelperText>
              <RadioGroup
                id="dichotomousVariables"
                name="dichotomousVariables"
                onChange={(e)=>props.readOnly ? null : handleMandatoryChange(e, "Yes", ["crossTabFile"])}
                value={state.dichotomousVariables}
              >
                <FormControlLabel
                  value="Yes"
                  control={<Radio color="primary" />}
                  label={yesAnswer}
                />
                <FormControlLabel
                  value="No"
                  control={<Radio color="primary" />}
                  label={noAnswer}
                />
              </RadioGroup>
            </FormControl>
            {state.dichotomousVariables==="Yes" &&
              <Box className={classes.indentedSection} sx={{mb: 3}}>
                {supportingFilesSection("crossTabFile", `${classes.addBtn} ${classes.mb3}`)}
              </Box>
            }
            <FormControl
              sx={{mb: -1}}
              component="fieldset"
              required 
              error={Boolean(props.errors.filesList?.[`outputFiles.${index}.correlatedVariables`])}
              aria-describedby="correlatedVariables-helperText"
            >
              <FormLabel component="legend">
                {t("requestForm.modifyFile.correlatedVariables")}
                <span className="screen-reader-text">{t("inputs.required")}</span>
              </FormLabel>
              <FormHelperText id="correlatedVariables-helperText">
                {t(props.errors.filesList?.[`outputFiles.${index}.correlatedVariables`])}
              </FormHelperText>
              <RadioGroup
                id="correlatedVariables"
                name="correlatedVariables"
                onChange={(e)=>props.readOnly ? null : handleMandatoryChange(e, "Yes", ["subTotalsFile"])}
                value={state.correlatedVariables}
              >
                <FormControlLabel
                  value="Yes"
                  control={<Radio color="primary" />}
                  label={yesAnswer}
                />
                <FormControlLabel
                  value="No"
                  control={<Radio color="primary" />}
                  label={noAnswer}
                />
              </RadioGroup>
            </FormControl>
            {state.correlatedVariables==="Yes" &&
              <Box className={classes.indentedSection} sx={{mt: 3}}>
                {supportingFilesSection("subTotalsFile", `${classes.addBtn} ${classes.mb3}`,undefined,true,true)}
              </Box>
            }
          </Box>
        }
        <Divider sx={{mb: 3}} />
        <Typography variant="subtitle2" component="h3" sx={{mb: 3}}>
          {t('requestForm.modifyFile.rounding')}
        </Typography>
        <Grid component="span" container>
          <Grid component="span" xs item>
            <FormControl
              sx={{mb: 2}}
              component="fieldset"
              required 
              disabled={fileFlag} 
              error={Boolean(props.errors.filesList?.[`outputFiles.${index}.roundingOutput`])}
              aria-describedby="roundingOutput-helperText"
            >
              <FormLabel component="legend" className={classes.tooltipLabel}>
                {t("requestForm.modifyFile.roundingOutput")}
                <span className="screen-reader-text">{t("inputs.required")}</span>
              </FormLabel>
              <FormHelperText id="roundingOutput-helperText">
                {t(props.errors.filesList?.[`outputFiles.${index}.roundingOutput`])}
              </FormHelperText>
              <RadioGroup
                id="roundingOutput"
                name="roundingOutput"
                value={state.roundingOutput}
                onChange={(e)=>props.readOnly ? null : handleMandatoryChange(e, "Yes", ["unroundedFile"])}
              >
                <FormControlLabel
                  value="Yes"
                  control={<Radio color="primary" />}
                  label={yesAnswer}
                />
                <FormControlLabel
                  value="No"
                  control={<Radio color="primary" />}
                  label={noAnswer}
                />
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid component="span" className={classes.legendInfoIcon} item>
            <Tooltip title={t("requestForm.modifyFile.roundingOutputTip")} arrow describeChild>
              <IconButton
                aria-label={t('requestForm.toolTipMoreInformation')}
                edge="end"
                size="large">
                <Icon path={mdiInformationOutline} size={1} />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
        {state.roundingOutput === 'Yes' && 
          <Box className={classes.indentedSection} sx={{mb: 3}}>
            <Box sx={{mb: 3}}>
              <CutCopyPasteTextField
                id="roundingOutputDescription"
                name="roundingOutputDescription"
                multiline
                minRows="3"
                margin="dense"
                label={t("requestForm.modifyFile.roundingOutputDescription")}
                variant="outlined"
                error={Boolean(props.errors.filesList?.[`outputFiles.${index}.roundingOutputDescription`])}
                helperText={t(props.errors.filesList?.[`outputFiles.${index}.roundingOutputDescription`])}
                required
                fullWidth
                inputProps={{readOnly: props.readOnly}}
                value={state.roundingOutputDescription}
                onChange={handleStateValueChange}
              />
            </Box>
            {supportingFilesSection("unroundedFile", `${classes.addBtn} ${classes.mb3}`)}
          </Box>
        }
        <Divider sx={{mb: 3}} />
        <Typography component="h3" variant="subtitle2" sx={{mb: 3}}>
          {t("requestForm.modifyFile.economicData")}
        </Typography>
        <Grid component="span" container>
          <Grid component="span" xs item>
            <FormControl
              sx={{mb: 2}}
              component="fieldset"
              required 
              disabled={fileFlag} 
              error={Boolean(props.errors.filesList?.[`outputFiles.${index}.economicDataOutput`])}
              aria-describedby="economicDataOutput-helperText"
            >
              <FormLabel component="legend" className={classes.tooltipLabel}>
                {t("requestForm.modifyFile.economicDataOutput")}
                <span className="screen-reader-text">{t("inputs.required")}</span>
              </FormLabel>
              <FormHelperText id="economicDataOutput-helperText">
                {t(props.errors.filesList?.[`outputFiles.${index}.economicDataOutput`])}
              </FormHelperText>
              <RadioGroup
                id="economicDataOutput"
                name="economicDataOutput"
                value={state.economicDataOutput}
                onChange={(e)=>props.readOnly ? null : handleMandatoryChange(e, null, ["continuousVariablesSensitivityFile"])}
              >
                <FormControlLabel
                  value="Yes"
                  control={<Radio color="primary" />}
                  label={yesAnswer}
                />
                <FormControlLabel
                  value="No"
                  control={<Radio color="primary" />}
                  label={noAnswer}
                />
              </RadioGroup>
            </FormControl>
        {state.economicDataOutput === "Yes" &&
          <Box className={classes.indentedSection} sx={{pb: 3, mb: 3}}>
            <FormControl
              sx={{mb: 2}}
              component="fieldset"
              required 
              error={Boolean(props.errors.filesList?.[`outputFiles.${index}.uniqueDataUnits`])}
              aria-describedby="uniqueDataUnits-helperText"
            >
              <FormLabel component="legend">
                {t("requestForm.modifyFile.uniqueDataUnits")}
                <span className="screen-reader-text">{t("inputs.required")}</span>
              </FormLabel>
              <FormHelperText id="uniqueDataUnits-helperText">
                {t(props.errors.filesList?.[`outputFiles.${index}.uniqueDataUnits`])}
              </FormHelperText>
              <RadioGroup
                id="uniqueDataUnits"
                name="uniqueDataUnits"
                onChange={props.readOnly ? null : handleStateValueChange} 
                value={state.uniqueDataUnits}
              >
                <FormControlLabel
                  value="Yes"
                  control={<Radio color="primary" />}
                  label={yesAnswer}
                />
                <FormControlLabel
                  value="No"
                  control={<Radio color="primary" />}
                  label={noAnswer}
                />
              </RadioGroup>
            </FormControl>
            {state.uniqueDataUnits==="No" && 
              <Alert
              role="note"
              severity="info"
              className={classes.alert}
              sx={{mb: 3}}

            >
              <Typography component="p" variant="body2" sx={{mb: 2}}>
                {t('requestForm.modifyFile.reviseOutput')}
              </Typography>
              
            </Alert>
            }
            <FormControl
              sx={{mb: 2}}
              component="fieldset"
              required 
              error={Boolean(props.errors.filesList?.[`outputFiles.${index}.outputContinuousVariables`])}
              aria-describedby="outputContinuousVariables-helperText"
            >
              <FormLabel component="legend">
                {t("requestForm.modifyFile.outputContinuousVariables")}
                <span className="screen-reader-text">{t("inputs.required")}</span>
              </FormLabel>
              <FormHelperText id="outputContinuousVariables-helperText">
                {t(props.errors.filesList?.[`outputFiles.${index}.outputContinuousVariables`])}
              </FormHelperText>
              <RadioGroup
                id="outputContinuousVariables"
                name="outputContinuousVariables"
                onChange={(e)=>props.readOnly ? null : handleMandatoryChange(e, null, ["continuousVariablesSensitivityFile"])}
                value={state.outputContinuousVariables}
              >
                <FormControlLabel
                  value="Yes"
                  control={<Radio color="primary" />}
                  label={yesAnswer}
                />
                <FormControlLabel
                  value="No"
                  control={<Radio color="primary" />}
                  label={noAnswer}
                />
              </RadioGroup>
            </FormControl>
          {state.outputContinuousVariables==="Yes" &&
          <Box className={classes.indentedSection} sx={{pb: 3, mb: 3}}>
            <FormControl
            sx={{mb: 2}}
            component="fieldset"
            required 
            error={Boolean(props.errors.filesList?.[`outputFiles.${index}.continuousVariablesSensitivity`])}
            aria-describedby="continuousVariablesSensitivity-helperText">

            <FormLabel component="legend">
              {t("requestForm.modifyFile.continuousVariablesSensitivity")}
              <span className="screen-reader-text">{t("inputs.required")}</span>
            </FormLabel>
            <FormHelperText id="continuousVariablesSensitivity-helperText">
              {t(props.errors.filesList?.[`outputFiles.${index}.continuousVariablesSensitivity`])}
            </FormHelperText>
            <RadioGroup
              id="continuousVariablesSensitivity"
              name="continuousVariablesSensitivity"
              onChange={(e)=>props.readOnly ? null : handleMandatoryChange(e, "Yes", ["continuousVariablesSensitivityFile"])}
              value={state.continuousVariablesSensitivity}
            >
              <FormControlLabel
                value="Yes"
                control={<Radio color="primary" />}
                label={yesAnswer}
              />
              <FormControlLabel
                value="No"
                control={<Radio color="primary" />}
                label={noAnswer}
              />
            </RadioGroup>
            </FormControl>
            {state.continuousVariablesSensitivity==="Yes" && 
                  <Box className={classes.indentedSection} sx={{mb:3}}>
                      {supportingFilesSection("continuousVariablesSensitivityFile", `${classes.addBtn} ${classes.mb3}`,undefined,true,false,true)}
                    </Box>
            }
            {state.continuousVariablesSensitivity==="No" &&
                         
                        <Alert
                        role="note"
                        severity="info"
                        className={classes.alert}
                        sx={{mb: 3}}
          
                      >
                        <Typography component="p" variant="body2" sx={{mb: 2}}>
                          {t('requestForm.modifyFile.reviseOutput')}
                        </Typography>
                        
                      </Alert>
                      
            }
          </Box>
          }
          </Box>
        }
          </Grid>
        </Grid>
        <Divider sx={{mb: 3}} />
        <Typography component="h3" variant="subtitle2" sx={{mb: 3}}>
          {t("requestForm.modifyFile.additionalSupportingFiles")}
        </Typography>
        {supportingFilesSection("other", classes.addBtn)}
      </div>
      <div className={classes.footer}>
        {props.readOnly &&
          <Button
            id="cancel-output-btn"
            variant="contained"
            color="primary"
            onClick={()=>props.toggleDrawer(false)}
          >
            {t("requestForm.close")}
          </Button>
        }
        {!props.readOnly &&
          <>
            <Button
              id="cancel-output-btn"
              sx={{mr: 2}}
              variant="outlined"
              color="primary"
              onClick={()=>props.toggleDrawer(false)}
            >
              {t("requestForm.cancel")}
            </Button>
            <Button 
              id="save-output-btn"
              variant="contained"
              color="primary"
              onClick={props.index !== null ? 
                ()=>validateOutput(()=>props.handleEditOutput(index, {...state})) : 
                ()=>validateOutput(()=>props.handleAddNew({...state}))
              }
              disabled={fileFlag}
            >
              {props.index !== null ? 
                t("requestForm.modifyFile.update") : 
                t("requestForm.modifyFile.add")
              }
            </Button>
          </>
        }
      </div>
      <DialogOutputMethodHelp 
        toggleDialog={()=> setDialogOutputMethodHelp(false)}
        open={dialogOutputMethodHelp}
      />
      <AlertDialog
        openAlert={dialogAlert.open}
        handleAlertClose={()=>setDialogAlert({open: false, event: null})}
        titleMessage={t("requestForm.modifyFile.keepAnswersTitle")}
        descriptionMessage={t("requestForm.modifyFile.keepAnswers")}
      >
        <Button
          onClick={()=>handleOutputChange(dialogAlert.event)}
          color="primary"
          variant="outlined"
        >
          {t("requestForm.no")}
        </Button>
        <Button
          color="primary"
          variant="contained"
          className={classes.footerBtns}
          onClick={()=>{
            handleStateValueChange(dialogAlert.event);
            setDialogAlert({
              open: false,
              event: null
            });
          }}
        >
          {t("requestForm.yes")}
        </Button>
      </AlertDialog>
      <Snackbar 
        message={snackbarState.message}
        severity={snackbarState.severity}
        open={snackbarState.open}
        handleClose={()=>{setSnackbarState({...snackbarState, open: false, message: ""})}}
      />
    </Root>
  );
}
ModifyFileForm.propTypes={
  toggleDrawer: PropTypes.func.isRequired,
  formData: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  setErrors: PropTypes.func.isRequired,
  filesList: PropTypes.arrayOf(PropTypes.string).isRequired,
  readOnly: PropTypes.bool.isRequired,
  analystEditFlag: PropTypes.bool.isRequired,
  isAnalyst: PropTypes.bool.isRequired,
  index: PropTypes.number,
  handleAddNew: PropTypes.func.isRequired,
  handleEditOutput: PropTypes.func.isRequired,
}

const PREFIX = 'ModifyFile';

const classes = {
  drawerHeader: `${PREFIX}-drawerHeader`,
  drawerContent: `${PREFIX}-drawerContent`,
  footer: `${PREFIX}-footer`,
  addBtn: `${PREFIX}-addBtn`,
  autocompleteOption: `${PREFIX}-autocompleteOption`,
  filePath: `${PREFIX}-filePath`,
  filePathItem: `${PREFIX}-filePathItem`,
  indentedSection: `${PREFIX}-indentedSection`,
  legendInfoIcon: `${PREFIX}-legendInfoIcon`,
  textFieldInfoIcon: `${PREFIX}-textFieldInfoIcon`,
  mb3: `${PREFIX}-mb3`,
};

const Root = styled('div')(({theme}) => ({
  [`& .${classes.drawerHeader}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: theme.palette.common.white,
    margin: theme.spacing(0, -3, 3, 0),
    boxShadow: theme.shadows[0],
    borderBottom: '1px solid',
    borderBottomColor: theme.palette.divider,
    position: 'fixed', 
    top: 0,
    zIndex: 500,
    width: '100%',
    maxWidth: theme.spacing(75),
    boxSizing: 'border-box',
    padding: theme.spacing(1.5, 3),
  },

  [`& .${classes.drawerContent}`]: {
    marginTop: theme.spacing(8),
    marginBottom: theme.spacing(8),
    padding: theme.spacing(3),
    overflowY: 'auto',
    overflowX: 'hidden',
    height: '100%',
  },

  [`& .${classes.footer}`]: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginRight: theme.spacing(-3),
    padding: theme.spacing(1.75, 3),
    borderTop: '1px solid',
    borderTopColor: theme.palette.divider,
    position: 'fixed',
    bottom: 0,
    width: '100%',
    maxWidth: theme.spacing(75),
    boxSizing: 'border-box',
    backgroundColor: theme.palette.common.white,
    zIndex: 500,
  },

  [`& .${classes.addBtn}`]: {
    'marginTop': theme.spacing(-1),
    'borderStyle': 'dashed',
    'width': '100%',
    'borderColor': alpha(theme.palette.common.black, 0.23),
    '&.MuiButton-outlinedPrimary:hover': {
      borderStyle: 'dashed',
    },
  },

  [`& .${classes.autocompleteOption}`]: {
    whiteSpace: 'normal',
    wordBreak: 'break-all',
  },

  [`& .${classes.filePath}`]: {
    display: 'flex',
    flexFlow: 'wrap',
    alignItems: 'flex-end',
  },

  [`& .${classes.filePathItem}`]: {
    display: 'flex',
    alignItems: 'center',
  },

  [`& .${classes.indentedSection}`]: {
    display: 'inline-block',
    width: '100%',
    boxSizing: 'border-box',
    paddingTop: theme.spacing(3),
    paddingLeft: theme.spacing(3),
    borderLeft: '1px solid',
    borderLeftColor: theme.palette.divider,
  },

  [`& .${classes.legendInfoIcon}`]: {
    marginTop: theme.spacing(-1.25),
    marginLeft: theme.spacing(1),
  },

  [`& .${classes.textFieldInfoIcon}`]: {
    paddingLeft: theme.spacing(1),
  },

  [`& .${classes.mb3}`]: {
    marginBottom: theme.spacing(3),
  }
}));

export default function ModifyFile(props){
  return (
    <StyledDrawer anchor="right" open={props.open}>
      <ModifyFileForm {...props} />
    </StyledDrawer>
  );
}
ModifyFile.propTypes={
  toggleDrawer: PropTypes.func.isRequired,
  formData: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  setErrors: PropTypes.func.isRequired,
  filesList: PropTypes.arrayOf(PropTypes.string).isRequired,
  readOnly: PropTypes.bool.isRequired,
  analystEditFlag: PropTypes.bool.isRequired,
  isAnalyst: PropTypes.bool.isRequired,
  index: PropTypes.number,
  handleAddNew: PropTypes.func.isRequired,
  handleEditOutput: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
}